/**
 * Role-Based Access Control (RBAC) Types
 *
 * This file defines role types, groups, and helper functions for the OptoPro frontend.
 *
 * ## Primary Roles
 * - **RECEPTIONIST**: Front desk staff handling appointments and check-ins
 * - **CLINICIAN**: Eye care professionals performing examinations
 * - **MANAGER**: Practice managers with administrative access
 *
 * ## Important Notes
 * - These role values MUST match the backend RolesEnum values exactly
 * - When the backend enum changes, update this file to match
 * - See backend: optopro-backend/src/common/decorators/roles.decorator.ts
 *
 * For comprehensive documentation, see: src/auth/docs/RBAC.md
 *
 * @example
 * ```typescript
 * import { Role, useHasRole } from './auth/types/roles';
 *
 * function MyComponent() {
 *   const isManager = useHasRole(Role.MANAGER);
 *
 *   return isManager ? <AdminPanel /> : <UserPanel />;
 * }
 * ```
 */

/**
 * Role Enum
 *
 * Defines all roles available in the OptoPro system.
 * These values MUST match the backend RolesEnum exactly.
 *
 * @see backend: optopro-backend/src/common/decorators/roles.decorator.ts
 */
export enum Role {
  // ==================== PRIMARY ROLES ====================
  // Use these in all new code

  /**
   * Receptionist - Front desk staff
   *
   * Typical permissions:
   * - View and create appointments
   * - View and edit patient demographics
   * - Check-in patients
   * - Access booking calendar
   */
  RECEPTIONIST = 'receptionist',

  /**
   * Clinician - Eye care professional
   *
   * Typical permissions:
   * - All receptionist permissions
   * - Clinical examination modules
   * - Refraction and prescreening
   * - Contact lens fitting and aftercare
   * - Diabetic eye screening, MECS/CUES
   */
  CLINICIAN = 'clinician',

  /**
   * Manager - Practice manager
   *
   * Typical permissions:
   * - All receptionist and clinician permissions
   * - User creation and management
   * - Financial reports and analytics
   * - Till/POS management
   * - Marketing campaigns
   * - Organization settings
   */
  MANAGER = 'manager',

  // ==================== LEGACY ALIASES ====================
  // Backward compatibility - prefer primary roles in new code

  /**
   * @deprecated Use RECEPTIONIST instead
   * Legacy alias for receptionist role
   */
  ASSISTANT = 'assistant',

  /**
   * @deprecated Use CLINICIAN instead
   * Legacy alias for clinician role
   */
  PROFESSIONAL = 'professional',

  /**
   * @deprecated Use MANAGER instead
   * Legacy alias for manager role
   */
  ACCOUNTANT = 'accountant',

  // ==================== ADDITIONAL ROLES ====================

  /**
   * Admin - System administrator
   */
  ADMIN = 'admin',

  /**
   * Patient - Patient portal user
   */
  PATIENT = 'patient',

  /**
   * Lab - Laboratory staff
   */
  LAB = 'lab',

  /**
   * Marketing - Marketing staff
   */
  MARKETING = 'marketing',

  /**
   * System Manager - System-level manager
   */
  SYS_MANAGER = 'sys_manager',
}

/**
 * Role Groups
 *
 * Predefined groups of roles for common permission sets.
 * Use these instead of manually listing multiple roles.
 *
 * @example
 * ```typescript
 * import { RoleGroup, useHasRole } from './auth/types/roles';
 *
 * function BookingPanel() {
 *   const canBook = useHasRole(RoleGroup.BOOKING_STAFF);
 *
 *   if (!canBook) return null;
 *   return <AppointmentBooking />;
 * }
 * ```
 */
export const RoleGroup = {
  /**
   * Staff who handle appointment booking and patient check-in
   * Includes: RECEPTIONIST, ASSISTANT
   */
  BOOKING_STAFF: [Role.RECEPTIONIST, Role.ASSISTANT] as const,

  /**
   * Clinical staff who perform examinations and provide eye care
   * Includes: CLINICIAN, PROFESSIONAL
   */
  CLINICAL_STAFF: [Role.CLINICIAN, Role.PROFESSIONAL] as const,

  /**
   * Management staff with administrative and financial access
   * Includes: MANAGER, ACCOUNTANT
   */
  MANAGEMENT_STAFF: [Role.MANAGER, Role.ACCOUNTANT] as const,

  /**
   * All staff members (excludes patients)
   * Includes: all receptionists, clinicians, managers, and specialized staff
   */
  ALL_STAFF: [
    Role.RECEPTIONIST,
    Role.ASSISTANT,
    Role.CLINICIAN,
    Role.PROFESSIONAL,
    Role.MANAGER,
    Role.ACCOUNTANT,
    Role.ADMIN,
    Role.LAB,
    Role.MARKETING,
    Role.SYS_MANAGER,
  ] as const,

  /**
   * Roles with full administrative privileges
   * Includes: MANAGER, ADMIN, SYS_MANAGER
   */
  ADMIN_ROLES: [Role.MANAGER, Role.ADMIN, Role.SYS_MANAGER] as const,
} as const;

/**
 * Type representing any valid role group
 */
export type RoleGroupType = (typeof RoleGroup)[keyof typeof RoleGroup];

/**
 * Type for a single role or array of roles
 */
export type RoleOrRoles = Role | readonly Role[];

/**
 * Normalizes a role ID to its primary equivalent.
 * Converts legacy aliases to their corresponding primary roles.
 *
 * @param roleId - The role ID to normalize (can be null/undefined)
 * @returns The primary role equivalent, or the original role if no mapping exists
 *
 * @example
 * ```typescript
 * normalizeToPrimaryRole('assistant')     // returns Role.RECEPTIONIST
 * normalizeToPrimaryRole('professional')  // returns Role.CLINICIAN
 * normalizeToPrimaryRole('accountant')    // returns Role.MANAGER
 * normalizeToPrimaryRole('receptionist')  // returns Role.RECEPTIONIST
 * normalizeToPrimaryRole(null)            // returns null
 * ```
 */
export function normalizeToPrimaryRole(roleId: string | null | undefined): Role | null {
  if (!roleId) return null;

  const roleMap: Record<string, Role> = {
    [Role.ASSISTANT]: Role.RECEPTIONIST,
    [Role.PROFESSIONAL]: Role.CLINICIAN,
    [Role.ACCOUNTANT]: Role.MANAGER,
  };

  return roleMap[roleId as Role] || (roleId as Role);
}

/**
 * Checks if a role belongs to a specific group.
 *
 * @param roleId - The role ID to check (can be null/undefined)
 * @param group - The role group to check against
 * @returns True if the role is in the group, false otherwise
 *
 * @example
 * ```typescript
 * isRoleInGroup('receptionist', RoleGroup.BOOKING_STAFF)  // returns true
 * isRoleInGroup('clinician', RoleGroup.BOOKING_STAFF)     // returns false
 * isRoleInGroup(null, RoleGroup.BOOKING_STAFF)            // returns false
 * ```
 */
export function isRoleInGroup(
  roleId: string | null | undefined,
  group: readonly Role[]
): boolean {
  if (!roleId) return false;
  return group.includes(roleId as Role);
}

/**
 * Gets the primary role category for a given role ID.
 * Returns null if the role doesn't map to a primary category.
 *
 * @param roleId - The role ID to categorize
 * @returns The primary role category or null
 *
 * @example
 * ```typescript
 * getPrimaryRoleCategory('assistant')     // returns Role.RECEPTIONIST
 * getPrimaryRoleCategory('professional')  // returns Role.CLINICIAN
 * getPrimaryRoleCategory('patient')       // returns null
 * ```
 */
export function getPrimaryRoleCategory(roleId: string | null | undefined): Role | null {
  if (!roleId) return null;

  const normalized = normalizeToPrimaryRole(roleId);

  const primaryRoles = [Role.RECEPTIONIST, Role.CLINICIAN, Role.MANAGER];

  if (normalized && primaryRoles.includes(normalized)) {
    return normalized;
  }

  return null;
}

/**
 * Checks if a role is a staff role (excludes patients and external users).
 *
 * @param roleId - The role ID to check
 * @returns True if the role is a staff role, false otherwise
 *
 * @example
 * ```typescript
 * isStaffRole('receptionist')  // returns true
 * isStaffRole('clinician')     // returns true
 * isStaffRole('patient')       // returns false
 * isStaffRole(null)            // returns false
 * ```
 */
export function isStaffRole(roleId: string | null | undefined): boolean {
  return isRoleInGroup(roleId, RoleGroup.ALL_STAFF);
}

/**
 * Checks if a role has administrative privileges.
 *
 * @param roleId - The role ID to check
 * @returns True if the role has admin privileges, false otherwise
 *
 * @example
 * ```typescript
 * isAdminRole('manager')      // returns true
 * isAdminRole('sys_manager')  // returns true
 * isAdminRole('receptionist') // returns false
 * ```
 */
export function isAdminRole(roleId: string | null | undefined): boolean {
  return isRoleInGroup(roleId, RoleGroup.ADMIN_ROLES);
}

/**
 * Gets a human-readable display name for a role.
 *
 * @param roleId - The role ID
 * @returns Display name for the role
 *
 * @example
 * ```typescript
 * getRoleDisplayName('receptionist')  // returns 'Receptionist'
 * getRoleDisplayName('sys_manager')   // returns 'System Manager'
 * getRoleDisplayName(null)            // returns 'Unknown'
 * ```
 */
export function getRoleDisplayName(roleId: string | null | undefined): string {
  if (!roleId) return 'Unknown';

  const displayNames: Record<Role, string> = {
    [Role.RECEPTIONIST]: 'Receptionist',
    [Role.CLINICIAN]: 'Clinician',
    [Role.MANAGER]: 'Manager',
    [Role.ASSISTANT]: 'Assistant',
    [Role.PROFESSIONAL]: 'Professional',
    [Role.ACCOUNTANT]: 'Accountant',
    [Role.ADMIN]: 'Administrator',
    [Role.PATIENT]: 'Patient',
    [Role.LAB]: 'Lab',
    [Role.MARKETING]: 'Marketing',
    [Role.SYS_MANAGER]: 'System Manager',
  };

  return displayNames[roleId as Role] || roleId;
}

/**
 * Display names for all roles.
 * Use this for dropdowns, labels, etc.
 *
 * @example
 * ```typescript
 * <Select>
 *   {Object.entries(ROLE_DISPLAY_NAMES).map(([roleId, name]) => (
 *     <MenuItem key={roleId} value={roleId}>{name}</MenuItem>
 *   ))}
 * </Select>
 * ```
 */
export const ROLE_DISPLAY_NAMES: Record<Role, string> = {
  [Role.RECEPTIONIST]: 'Receptionist',
  [Role.CLINICIAN]: 'Clinician',
  [Role.MANAGER]: 'Manager',
  [Role.ASSISTANT]: 'Assistant',
  [Role.PROFESSIONAL]: 'Professional',
  [Role.ACCOUNTANT]: 'Accountant',
  [Role.ADMIN]: 'Administrator',
  [Role.PATIENT]: 'Patient',
  [Role.LAB]: 'Lab',
  [Role.MARKETING]: 'Marketing',
  [Role.SYS_MANAGER]: 'System Manager',
};

/**
 * Type guard to check if a string is a valid Role
 *
 * @param value - The value to check
 * @returns True if the value is a valid Role enum value
 *
 * @example
 * ```typescript
 * const userRole = getUserRoleFromAPI();
 * if (isValidRole(userRole)) {
 *   // TypeScript now knows userRole is of type Role
 *   console.log(ROLE_DISPLAY_NAMES[userRole]);
 * }
 * ```
 */
export function isValidRole(value: any): value is Role {
  return Object.values(Role).includes(value);
}

/**
 * Checks if a user has any of the specified roles.
 *
 * @param userRoleId - The user's role ID
 * @param allowedRoles - Single role, array of roles, or role group
 * @returns True if the user has any of the allowed roles
 *
 * @example
 * ```typescript
 * hasAnyRole('receptionist', [Role.RECEPTIONIST, Role.MANAGER])  // true
 * hasAnyRole('clinician', Role.MANAGER)                          // false
 * hasAnyRole('assistant', RoleGroup.BOOKING_STAFF)               // true
 * ```
 */
export function hasAnyRole(
  userRoleId: string | null | undefined,
  allowedRoles: RoleOrRoles | RoleGroupType
): boolean {
  if (!userRoleId) return false;

  const rolesArray = Array.isArray(allowedRoles) ? allowedRoles : [allowedRoles];

  return rolesArray.includes(userRoleId as Role);
}

/**
 * Checks if a user has all of the specified roles.
 * Note: In practice, a user can only have one role, so this typically returns
 * true only if a single role is specified and matches.
 *
 * @param userRoleId - The user's role ID
 * @param requiredRoles - Array of required roles
 * @returns True if the user has all required roles
 *
 * @example
 * ```typescript
 * hasAllRoles('manager', [Role.MANAGER])  // true
 * hasAllRoles('manager', [Role.MANAGER, Role.CLINICIAN])  // false (user can only have one role)
 * ```
 */
export function hasAllRoles(
  userRoleId: string | null | undefined,
  requiredRoles: readonly Role[]
): boolean {
  if (!userRoleId) return false;

  // Since users can only have one role, this checks if the single role
  // is in the required roles list and the list has only that role
  return requiredRoles.length === 1 && requiredRoles.includes(userRoleId as Role);
}
